import { parseJSON } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Card,
    Chip,
    Skeleton,
    Stack,
    Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';

import { Order } from '@barboxx/models';
import { BusinessPickupMode } from '@barboxx/types';

import { BaseTextInput } from './BaseTextInput';

const PICKUP_MODE_LABEL: Record<BusinessPickupMode, string> = {
    Swipe: 'Swipe',
    Nfc: 'NFC',
    NfcWithFace: 'NFC + Face',
};

export function PickupDetailsCard({
    order,
    imageUrls,
    imageUrlsLoading = false,
    pickupDetailsExpanded,
    onPickupDetailsExpandedChange,
}: {
    order: Order.DetailsResponse;
    imageUrls?: Order.ImageUrlsResponse;
    imageUrlsLoading?: boolean;
    pickupDetailsExpanded: boolean;
    onPickupDetailsExpandedChange: (expanded: boolean) => void;
}) {
    return (
        <Card>
            <Accordion
                expanded={pickupDetailsExpanded}
                onChange={(_event, expanded) =>
                    onPickupDetailsExpandedChange(expanded)
                }
                TransitionProps={{ unmountOnExit: true }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                        px: 3,
                        py: 1,
                        minHeight: 72,
                        '& .MuiAccordionSummary-content': {
                            my: 0,
                            alignItems: 'center',
                        },
                        '&.Mui-expanded': {
                            minHeight: 72,
                        },
                        '&.Mui-expanded .MuiAccordionSummary-content': {
                            my: 0,
                        },
                    }}
                >
                    <Stack
                        direction="row"
                        gap={1}
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        <Typography variant="h6">Pickup Details</Typography>
                        <Chip
                            size="small"
                            variant="outlined"
                            label={
                                order.business?.pickupMode
                                    ? (PICKUP_MODE_LABEL[
                                          order.business
                                              .pickupMode as BusinessPickupMode
                                      ] ?? order.business.pickupMode)
                                    : '—'
                            }
                        />
                        <Chip
                            size="small"
                            variant="outlined"
                            label={`Locker: ${order.locker?.name ?? order.lockerId}`}
                        />
                        {order.cubbyId != null ? (
                            <Chip
                                size="small"
                                variant="outlined"
                                label={`Cubby: ${order.cubbyId}`}
                            />
                        ) : null}
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack gap={2}>
                        <Stack direction="row" gap={2} flexWrap="wrap">
                            <BaseTextInput
                                label="Pickup Method"
                                value={
                                    order.business?.pickupMode
                                        ? (PICKUP_MODE_LABEL[
                                              order.business
                                                  .pickupMode as BusinessPickupMode
                                          ] ?? order.business.pickupMode)
                                        : '—'
                                }
                                sx={{ minWidth: 160 }}
                            />
                            <BaseTextInput
                                label="Locker"
                                value={order.locker?.name ?? order.lockerId}
                                sx={{ minWidth: 200 }}
                            />
                            {order.cubbyId != null && (
                                <BaseTextInput
                                    label="Cubby"
                                    value={order.cubbyId}
                                    sx={{ minWidth: 120 }}
                                />
                            )}
                        </Stack>

                        <Stack direction="row" gap={2} flexWrap="wrap">
                            {order.startedAt && (
                                <DateTimePicker
                                    label="Started At"
                                    value={parseJSON(order.startedAt)}
                                    format="MM/dd/yyyy hh:mm:ss aa"
                                    readOnly
                                    sx={{ minWidth: 240 }}
                                />
                            )}
                            {order.readyAt && (
                                <DateTimePicker
                                    label="Ready At"
                                    value={parseJSON(order.readyAt)}
                                    format="MM/dd/yyyy hh:mm:ss aa"
                                    readOnly
                                    sx={{ minWidth: 240 }}
                                />
                            )}
                        </Stack>

                        {imageUrlsLoading ? (
                            <Stack gap={1} alignItems="flex-start">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Order Photo
                                </Typography>
                                <Skeleton
                                    variant="rectangular"
                                    width={240}
                                    height={240}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Stack>
                        ) : imageUrls?.orderPhotoUrl ? (
                            <Stack gap={1} alignItems="flex-start">
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Order Photo
                                </Typography>
                                <Box
                                    component="img"
                                    src={imageUrls.orderPhotoUrl}
                                    alt="Order photo"
                                    sx={{
                                        width: 240,
                                        height: 240,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                />
                            </Stack>
                        ) : null}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        </Card>
    );
}
